import type { Action, AppState } from '../store';
import type { TenantBundle } from '../types';
import { findDay } from '../selectors';
import type { AppConfig, TabDef, TabTarget, WidgetInstance } from './types';
import { WIDGETS } from './registry';

/**
 * Navigation and page helpers shared by both apps.
 * Which tab is lit, what a tab press does, what a page's header says, and
 * whether a widget should show — decided once, so web and native cannot drift.
 */

export function targetKey(t: TabTarget): string {
  return t.kind === 'page' ? `page:${t.pageId}` : `screen:${t.screen}`;
}

/** What pressing a tab does. */
export function tabActions(t: TabTarget): Action[] {
  return t.kind === 'page'
    ? [{ type: 'openPage', pageId: t.pageId }]
    : [{ type: 'go', screen: t.screen }];
}

/**
 * Keys that should light a tab for the current screen, most specific first.
 * Detail screens belong to their parent — a guest's page lights Talent, the
 * cart lights Me — but only if no tab claims the screen directly.
 */
export function candidateKeys(state: Pick<AppState, 'screen' | 'pageId'>): string[] {
  switch (state.screen) {
    case 'home': return ['page:home'];
    case 'page': return [`page:${state.pageId}`];
    case 'talentDetail': return ['screen:talent'];
    case 'timeline': case 'sessionDetail': case 'journey': return [`screen:${state.screen}`, 'screen:schedule'];
    case 'events': return ['screen:events', 'page:home'];
    case 'cart': case 'tickets': case 'shop': case 'messages': return [`screen:${state.screen}`, 'screen:me'];
    case 'search': return ['screen:search'];
    default: return [`screen:${state.screen}`];
  }
}

export function activeTabId(config: AppConfig, state: Pick<AppState, 'screen' | 'pageId'>): string | null {
  const enabled = config.navigation.tabs.filter((t) => t.enabled);
  for (const key of candidateKeys(state)) {
    const hit = enabled.find((t) => targetKey(t.target) === key);
    if (hit) return hit.id;
  }
  return null;
}

export function enabledTabs(config: AppConfig): TabDef[] {
  return config.navigation.tabs.filter((t) => t.enabled);
}

const SCREEN_TITLES = new Set(['schedule', 'talent', 'hall', 'quests', 'me']);

/**
 * Substitutes header tokens: {dayLong} {day} {event} {dates}.
 * Unknown tokens are left as written so a typo is visible rather than blank.
 */
export function renderTemplate(tpl: string, bundle: TenantBundle, state: Pick<AppState, 'day'>): string {
  const day = findDay(bundle, state.day);
  const long = new Date(`${day.date}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  return tpl
    .replace(/\{dayLong\}/g, long)
    .replace(/\{day\}/g, day.label)
    .replace(/\{event\}/g, bundle.event.name)
    .replace(/\{dates\}/g, bundle.event.dateRange);
}

/**
 * Header text for the current screen, letting the config take over for pages
 * and letting a tab's label rename its own top-level screen — so calling the
 * tab "Programme" also calls the screen "Programme".
 */
export function headerOverride(
  config: AppConfig, bundle: TenantBundle,
  state: Pick<AppState, 'screen' | 'pageId' | 'day'>,
  fallback: { kicker: string; title: string },
): { kicker: string; title: string } {
  if (state.screen === 'home' || state.screen === 'page') {
    const id = state.screen === 'home' ? 'home' : state.pageId;
    const page = config.pages[id];
    if (page) {
      return { kicker: renderTemplate(page.kicker, bundle, state), title: page.title };
    }
  }
  if (SCREEN_TITLES.has(state.screen)) {
    const tab = config.navigation.tabs.find(
      (t) => t.enabled && t.target.kind === 'screen' && t.target.screen === state.screen,
    );
    if (tab) return { ...fallback, title: tab.label };
  }
  return fallback;
}

/** Whether a widget should render for this attendee, today. */
export function widgetVisible(
  inst: WidgetInstance, config: AppConfig,
  ctx: { hasBadge: boolean; day: string },
): boolean {
  if (inst.hidden) return false;
  const def = WIDGETS[inst.type];
  if (!def) return false;
  if (def.requires && !config.features[def.requires]) return false;
  if (inst.audience === 'badge' && !ctx.hasBadge) return false;
  if (inst.audience === 'nobadge' && ctx.hasBadge) return false;
  if (inst.days?.length && !inst.days.includes(ctx.day)) return false;
  return true;
}

/** Legacy content quick-link targets ('hall:map', 'events') to structured ones. */
export function parseLegacyTarget(s: string): import('../types').NavTarget {
  if (s.startsWith('hall:')) return { screen: 'hall', hallTab: s.split(':')[1] as 'map' | 'exhibitors' };
  return { screen: (s || 'home') as import('../types').NavTarget['screen'] };
}
